import type { ColumnDef } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import UserInitialsTableCell from '~/components/UserInitialsTableCell.vue'
import EsgSubmissionStatusUserInitials from '~/components/EsgSubmissionStatusUserInitials.vue'
import RejectionMessageIcon from '~/components/RejectionMessageIcon.vue'
import ExpandIcon from '~/components/ExpandIcon.vue'
import ExclamationIcon from '~/components/ExclamationIcon.vue'
import PenIcon from '~/components/PenIcon.vue'

export const createColumns = (filterOptions: EsgSubmissionsFilterOptions, isReviewing: boolean, onViewComments: (submissionId: string) => void): ColumnDef<EsgSubmission>[] => [
  {
    accessorKey: 'submission_id',
    id: 'submission_id',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

      return h('div', { class: 'flex justify-between items-center w-full whitespace-nowrap' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'submission ID'),
        h(Button, {
          class: 'text-main-primary',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
        }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
      ])
    },
    cell: ({ row, column }) => {
      const { $store } = useNuxtApp()
      const userId = $store.auth.authenticatedUser.id
      const isUserReviewer = row.original.reviewed_by.some(reviewer => reviewer.user_id === userId)
      const isSubmissionPending = row.original.status === 'PENDING'
      const isShowingPendingHyperlink = isReviewing && isUserReviewer && isSubmissionPending

      return [
        !isShowingPendingHyperlink && h('div', { class: 'text-left text-md text-foreground no-localize-translate' }, row.getValue(column.id)),
        isShowingPendingHyperlink && h('div', { class: 'flex items-center gap-1.5' }, [
          h(Button, {
            variant: 'link',
            class: 'underline text-foreground hover:text-theme-text-black p-0 text-md',
            onClick: () => navigateTo(`/dashboard/esg-data-submission/${row.original.id}/review`),
          }, () => row.getValue(column.id)),
          h('span', { class: 'text-theme-semantic-warning' }, h(ExclamationIcon, { customStyle: 'size-5' })),
        ]),
      ]
    },
  },
  {
    accessorKey: 'template.name',
    id: 'template',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'template name',
      filterData: filterOptions.templates,
    }),
    cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground' }, row.getValue(column.id)),
  },
  {
    accessorKey: 'asset.name',
    id: 'asset',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'asset',
      filterData: filterOptions.assets,
    }),
    cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground whitespace-nowrap' }, row.getValue(column.id)),
  },
  {
    accessorKey: 'metric.name',
    id: 'metric',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'metric',
      filterData: filterOptions.metrics,
    }),
    cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground whitespace-nowrap' }, row.getValue(column.id)),
  },
  {
    accessorKey: 'submitted_by.name',
    id: 'submitted_by_user',
    header: ({ column }) => {
      if (!isReviewing) {
        return h('span', { class: 'text-sm text-slate-800 font-semibold capitalize whitespace-nowrap' }, 'submitted by')
      }

      return h(DataTableFilterableHeader, {
        column,
        title: 'submitted by',
        filterData: filterOptions.submitted_by_users,
      })
    },
    cell: ({ row, column }) => h('div', { class: 'text-left text-md text-foreground' }, row.getValue(column.id)),
  },
  {
    accessorKey: 'reviewed_by',
    id: 'reviewed_by_user',
    size: 200,
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'reviewed by',
      filterData: filterOptions.reviewed_by_users,
    }),
    cell: ({ row }) => h(UserInitialsTableCell, {
      users: row.original.reviewed_by.map(({ user_id, user_name, decision }) => {
        return {
          id: user_id,
          name: user_name,
          decision,
        }
      }),
      initialsContainer: EsgSubmissionStatusUserInitials,
    }),
  },
  {
    accessorKey: 'last_updated_by.date',
    id: 'last_updated_at',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

      return h('div', { class: 'flex justify-between items-center w-full whitespace-nowrap' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'last updated on'),
        h(Button, {
          class: 'text-main-primary',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
        }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
      ])
    },
    cell: ({ row }) => {
      const { $moment } = useNuxtApp()
      const formattedDate = $moment(row.original.last_updated_by.date).format('DD/MM/YYYY')

      return h('div', { class: 'text-left text-md text-foreground no-localize-translate' }, formattedDate)
    },
  },
  {
    accessorKey: 'status',
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'status'),
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase()
      const comments = row.original.comments
      let statusClass = ''

      if (status === 'approved') {
        statusClass = 'text-theme-semantic-success'
      }
      else if (status === 'pending') {
        statusClass = 'text-theme-semantic-warning'
      }
      else if (status === 'rejected') {
        statusClass = 'text-theme-semantic-error'
      }

      const content = [
        status,
        comments.length > 0 && h(Button, {
          class: 'text-theme-semantic-error',
          variant: 'link',
          size: 'sm',
          onClick: () => {
            onViewComments(row.original.id)
          },
        }, () => h(RejectionMessageIcon, { customStyle: 'size-5' })),
      ]

      return h('div', { class: `text-left text-md capitalize flex items-center gap-1.5 ${statusClass}` }, content)
    },
  },
  {
    id: 'actions',
    header: () => h('span', { class: 'capitalize text-sm text-slate-800 font-semibold' }, 'actions'),
    cell: ({ row }) => {
      const { $store } = useNuxtApp()
      const { submitted_by, reviewed_by, status, id } = row.original
      const userId = $store.auth.authenticatedUser.id

      /**
       * Determine Action Allowed
       *
       * Edit:
       *  - only Submitter can edit Submission
       *  - only non-Approved Submissions
       *  - only when not reviewing
       *
       * Pending:
       *  - only Reviewer can set Submission to Pending
       *  - only when reviewing
       *
       * View:
       *  - Data owners can view their own Submissions
       *  - Admin can view all Submissions
       *  - Note: the Backend API should handle this already so View is the default action
       */
      const getActionType = () => {
        const isUserReviewer = reviewed_by.some(reviewer => reviewer.user_id === userId)
        const isSubmitter = submitted_by.id === userId
        const isPending = status === 'PENDING'
        const isApproved = status === 'APPROVED'

        if (!isReviewing && isSubmitter && !isApproved) return 'edit'
        if (isReviewing && isUserReviewer && isPending) return 'pending'
        return 'view'
      }

      const actionType = getActionType()

      switch (actionType) {
        case 'edit':
          return h(Button, {
            class: 'text-main-primary',
            variant: 'link',
            size: 'sm',
            onClick: () => navigateTo(`/dashboard/esg-data-submission/${id}/edit`),
          }, () => h(PenIcon))

        case 'pending':
          return h('div', { class: 'text-[#FABE13]' }, h(ExclamationIcon))

        case 'view':
          return h(Button, {
            class: 'text-main-primary',
            variant: 'link',
            size: 'sm',
            onClick: () => navigateTo(`/dashboard/esg-data-submission/${id}/view`),
          }, () => h(ExpandIcon))
      }
    },
  },
]
